import React, { useState } from "react";
import { useAuth } from "../../../components/Auth/AuthProvider";
import { useStripe } from "../../../hooks/useStripe"; // adjust path if needed
import styles from "./PricingPage.module.css";
import { useThemedIcon } from "../../../hooks/useThemedIcon"; // adjust path if needed
import checkIconLight from "../../../assets/check_active_light.svg";
import checkIconDark from "../../../assets/check_active_dark.svg";
import loadingButtonLight from "../../../assets/loading_button_dark.svg";
import loadingButtonDark from "../../../assets/loading_button_light.svg";
import billingLoadIconLight from "../../../assets/loading_light.svg";
import billingLoadIconDark from "../../../assets/loading_dark.svg";

interface PricingPageProps {
  onClose: () => void;
}

const PRICE_IDS = {
  hobbyist_monthly: "price_1T15YtI5kkwS8iLKnVX3pW1a",
  hobbyist_annual: "price_1T1AXiI5kkwS8iLKQhCQ3z8k",
  pro_monthly: "price_1T15ZcI5kkwS8iLKVLUZD1Zv",
  pro_annual: "price_1T1AYHI5kkwS8iLKREtjD1p9",
};

type PlanKey = keyof typeof PRICE_IDS;

const PricingPage: React.FC<PricingPageProps> = ({ onClose }) => {
  const { userProfile } = useAuth();
  const { subscribeToPlan } = useStripe();
  const [isAnnualHobbyist, setIsAnnualHobbyist] = useState(false);
  const [isAnnualPro, setIsAnnualPro] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<PlanKey | null>(null);
  const { getIcon } = useThemedIcon();
  const checkIcon = getIcon(checkIconLight, checkIconDark);
  const loadingButtonIcon = getIcon(loadingButtonLight, loadingButtonDark);
  const billingLoadIcon = getIcon(billingLoadIconLight, billingLoadIconDark);

  const handleSubscribe = async (planKey: PlanKey) => {
    setLoadingPlan(planKey);
    try {
      await subscribeToPlan(PRICE_IDS[planKey]);
    } finally {
      setLoadingPlan(null);
    }
  };

  const currentTier = (userProfile as any)?.subscriptionTier || "free";

  return (
    <div className={styles.container}>
      <div className={styles.scrollArea}>
        <div className={styles.pricingGrid}>
          {/* Free */}
          <div className={styles.pricingCard}>
            <div />
            <h3 className={styles.pricingTitle}>Free</h3>
            <p className={styles.pricingDescription}>
              Discover what Noah can do.
            </p>
            <div className={styles.pricingPriceRow}>
              <span className={styles.pricingPrice}>$0</span>
              <span className={styles.pricingSubText}>/free</span>
            </div>
            <div className={styles.togglePlaceholder} />
            <button
              className={`${styles.pricingButton} ${styles.pricingButtonDefault}`}
              disabled
            >
              {currentTier === "free" ? "Current plan" : "Free plan"}
            </button>
            <div className={styles.pricingFeatures}>
              <div className={styles.pricingFeaturesLabel}>Includes:</div>
              <div className={styles.pricingFeatureList}>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    5 daily generations (up to 30/month)
                  </span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    Unlimited .STL/.STEP downloads
                  </span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    Unlimited view-only links
                  </span>
                </div>
              </div>
            </div>
            <div />
          </div>

          {/* Hobbyist */}
          <div className={styles.pricingCard}>
            <div />
            <h3 className={styles.pricingTitle}>Hobbyist</h3>
            <p className={styles.pricingDescription}>
              Ideal for students, weekend builders, and first-time creators.
            </p>
            <div className={styles.pricingPriceRow}>
              <span className={styles.pricingPrice}>
                {isAnnualHobbyist ? "$5.99" : "$9.99"}
              </span>
              <span className={styles.pricingVat}>/month (inc. VAT)</span>
            </div>
            <div className={styles.toggleRow}>
              <div
                className={styles.toggleContainer}
                onClick={() => setIsAnnualHobbyist(!isAnnualHobbyist)}
              >
                {isAnnualHobbyist && (
                  <div className={styles.toggleCircleActive} />
                )}
              </div>
              <span className={styles.toggleLabel}>
                {isAnnualHobbyist ? "Annual (Save 40%)" : "Annual"}
              </span>
            </div>
            <button
              onClick={() =>
                handleSubscribe(
                  isAnnualHobbyist ? "hobbyist_annual" : "hobbyist_monthly",
                )
              }
              disabled={loadingPlan !== null || currentTier === "hobbyist"}
              className={`${styles.pricingButton} ${styles.pricingButtonDefault}`}
            >
              {loadingPlan === "hobbyist_monthly" ||
              loadingPlan === "hobbyist_annual" ? (
                <>
                  {isAnnualHobbyist ? "Get Hobbyist" : "Get Hobbyist"}
                  <img
                    src={billingLoadIcon}
                    alt="Loading"
                    className={styles.socialLoadingIcon}
                  />
                </>
              ) : currentTier === "hobbyist" ? (
                "Current plan"
              ) : (
                "Get Hobbyist"
              )}
            </button>
            <div className={styles.pricingFeatures}>
              <div className={styles.pricingFeaturesLabel}>
                Everything in Free, plus:
              </div>
              <div className={styles.pricingFeatureList}>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    150 monthly generations
                  </span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    5 daily generations (up to 150/month)
                  </span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>Credit rollovers</span>
                </div>
              </div>
            </div>
            <div />
          </div>

          {/* Pro */}
          <div className={styles.pricingCard}>
            <div />
            <h3 className={styles.pricingTitle}>Pro</h3>
            <p className={styles.pricingDescription}>
              For freelancers and independents delivering prototypes and client
              projects.
            </p>
            <div className={styles.pricingPriceRow}>
              <span className={styles.pricingPrice}>
                {isAnnualPro ? "$17.99" : "$29.99"}
              </span>
              <span className={styles.pricingVat}>/month (inc. VAT)</span>
            </div>
            <div className={styles.toggleRow}>
              <div
                className={styles.toggleContainer}
                onClick={() => setIsAnnualPro(!isAnnualPro)}
              >
                {isAnnualPro && <div className={styles.toggleCircleActive} />}
              </div>
              <span className={styles.toggleLabel}>
                {isAnnualPro ? "Annual (Save 40%)" : "Annual"}
              </span>
            </div>
            <button
              onClick={() =>
                handleSubscribe(isAnnualPro ? "pro_annual" : "pro_monthly")
              }
              disabled={loadingPlan !== null || currentTier === "pro"}
              className={`${styles.pricingButton} ${styles.pricingButtonPro}`}
            >
              {loadingPlan === "pro_monthly" || loadingPlan === "pro_annual" ? (
                <>
                  Get Pro
                  <img
                    src={loadingButtonIcon}
                    alt="Loading"
                    className={styles.submitLoadingIcon}
                  />
                </>
              ) : currentTier === "pro" ? (
                "Current plan"
              ) : (
                "Get Pro"
              )}
            </button>
            <div className={styles.pricingFeatures}>
              <div className={styles.pricingFeaturesLabel}>
                Everything in Hobbyist, plus:
              </div>
              <div className={styles.pricingFeatureList}>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    Unlimited generations
                  </span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>Priority support</span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    Access to new features
                  </span>
                </div>
              </div>
            </div>
            <div />
          </div>

          {/* Enterprise */}
          <div className={styles.pricingCard}>
            <div />
            <h3 className={styles.pricingTitle}>Enterprise</h3>
            <p className={styles.pricingDescription}>
              Built for organizations running collaborative, high-volume
              workflows.
            </p>
            <div className={styles.pricingPriceRow}>
              <span className={styles.pricingPrice}>Custom</span>
              <span className={styles.pricingSubText}>/seat</span>
            </div>
            <div className={styles.togglePlaceholder} />
            <a
              href="mailto:hello@noahcad.com"
              className={`${styles.pricingButton} ${styles.pricingButtonDefault}`}
            >
              Contact us
            </a>
            <div className={styles.pricingFeatures}>
              <div className={styles.pricingFeaturesLabel}>
                Everything in Pro, plus:
              </div>
              <div className={styles.pricingFeatureList}>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>SSO Enforcement</span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>Centralized admin</span>
                </div>
                <div className={styles.pricingFeatureItem}>
                  <img
                    src={checkIcon}
                    alt="✓"
                    style={{
                      width: "0.875rem",
                      height: "0.875rem",
                      flexShrink: 0,
                      marginTop: "0.1rem",
                    }}
                  />
                  <span className={styles.featureText}>
                    Dedicated success manager
                  </span>
                </div>
              </div>
            </div>
            <div />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
